import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { getAllBlogs, deleteBlog, getPendingReviewBlogs, approveBlog } from '../../services/blogService';

const BlogDashboard = ({ embedded = false }) => {
  const navigate = useNavigate();
  const [blogs, setBlogs] = useState([]);
  const [pendingBlogs, setPendingBlogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [deleting, setDeleting] = useState(false);
  const [approving, setApproving] = useState(null);

  useEffect(() => {
    fetchBlogs();
  }, []);

  const fetchBlogs = async () => {
    try {
      setLoading(true);
      setError(null);
      const [blogData, pending] = await Promise.all([
        getAllBlogs(),
        getPendingReviewBlogs()
      ]);
      setBlogs(blogData || []);
      setPendingBlogs(pending || []);
    } catch (err) {
      console.error('Error fetching blogs:', err);
      // If it's a permission error or collection doesn't exist, just show empty state
      if (err.message?.includes('Missing or insufficient permissions') || 
          err.message?.includes('collection') ||
          err.code === 'permission-denied') {
        setBlogs([]);
        setError(null);
      } else {
        setError('Failed to load blogs');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'Unknown';
    
    let date;
    if (timestamp.toDate) {
      date = timestamp.toDate();
    } else {
      date = new Date(timestamp);
    }
    
    return date.toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const handleDelete = async (blogId) => {
    if (deleteConfirm !== blogId) {
      setDeleteConfirm(blogId);
      return;
    }

    try {
      setDeleting(true);
      await deleteBlog(blogId);
      setBlogs(blogs.filter(blog => blog.id !== blogId));
      setDeleteConfirm(null);
    } catch (err) {
      console.error('Error deleting blog:', err);
      alert('Failed to delete blog. Please try again.');
    } finally {
      setDeleting(false);
    }
  };

  const handleApproveBlog = async (blogId) => {
    try {
      setApproving(blogId);
      await approveBlog(blogId);
      await fetchBlogs();
    } catch (err) {
      console.error('Error approving blog:', err);
      alert('Failed to approve blog. Please try again.');
    } finally {
      setApproving(null);
    }
  };

  const truncateText = (text, maxLength = 100) => {
    if (!text) return '';
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength).trim() + '...';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-400 mx-auto mb-4"></div>
          <p className="text-gray-400">Loading blogs...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-12">
        <p className="text-red-400 mb-4">{error}</p>
        <button 
          onClick={fetchBlogs}
          className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-colors"
        >
          Try Again
        </button>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      {!embedded ? (
        <div className="flex justify-between items-center">
          <div>
            <h2 className="text-3xl font-bold text-gray-100">Blog Posts</h2>
            <p className="text-gray-400 mt-2">Manage your TrendingChess blog content</p>
          </div>
          <div className="flex gap-3">
            <button
              onClick={() => navigate('/admin')}
              className="bg-gray-700 hover:bg-gray-600 text-white px-6 py-3 rounded-lg transition-colors inline-flex items-center gap-2"
            >
              ← Admin Dashboard
            </button>
            <button
              onClick={() => navigate('/admin/blog/new')}
              className="bg-green-600 hover:bg-green-700 text-white px-6 py-3 rounded-lg transition-colors inline-flex items-center gap-2"
            >
              <span>+</span>
              New Blog Post
            </button>
          </div>
        </div>
      ) : (
        <div className="flex justify-end">
          <button
            onClick={() => navigate('/admin/blog/new')}
            className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg transition-colors inline-flex items-center gap-2 text-sm"
          >
            <span>+</span>
            New Blog Post
          </button>
        </div>
      )}

      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gray-800 p-6 rounded-lg border border-gray-700">
          <h3 className="text-lg font-semibold text-gray-300 mb-2">Total Posts</h3>
          <p className="text-3xl font-bold text-blue-400">{blogs.length}</p>
        </div>
        <div className="bg-gray-800 p-6 rounded-lg border border-gray-700">
          <h3 className="text-lg font-semibold text-gray-300 mb-2">Published</h3>
          <p className="text-3xl font-bold text-green-400">
            {blogs.filter(blog => blog.published).length}
          </p>
        </div>
        <div className="bg-gray-800 p-6 rounded-lg border border-gray-700">
          <h3 className="text-lg font-semibold text-gray-300 mb-2">Drafts</h3>
          <p className="text-3xl font-bold text-yellow-400">
            {blogs.filter(blog => !blog.published).length}
          </p>
        </div>
      </div>

      {/* Pending Review Section */}
      {pendingBlogs.length > 0 && (
        <div className="bg-yellow-900/20 border border-yellow-700 rounded-lg p-6">
          <h3 className="text-xl font-semibold text-yellow-300 mb-4">
            Pending Review ({pendingBlogs.length})
          </h3>
          <p className="text-gray-400 text-sm mb-4">AI-drafted blogs awaiting approval</p>
          <div className="space-y-3">
            {pendingBlogs.map(blog => (
              <div key={blog.id} className="flex items-center justify-between bg-gray-800 rounded-lg p-4">
                <div className="flex-1 min-w-0">
                  <h4 className="text-gray-100 font-medium truncate">{blog.title}</h4>
                  <p className="text-gray-400 text-sm truncate">{truncateText(blog.excerpt || blog.body)}</p>
                  <div className="flex items-center gap-2 mt-1">
                    <span className="text-xs text-gray-500">{blog.source === 'ai_agent' ? 'AI Agent' : blog.source}</span>
                    <span className="text-xs text-gray-600">|</span>
                    <span className="text-xs text-gray-500">{formatDate(blog.createdAt)}</span>
                  </div>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <button
                    onClick={() => navigate(`/admin/blog/edit/${blog.id}`)}
                    className="text-blue-400 hover:text-blue-300 px-3 py-1.5 rounded transition-colors text-sm"
                  >
                    Edit
                  </button>
                  <button
                    onClick={() => handleApproveBlog(blog.id)}
                    disabled={approving === blog.id}
                    className="bg-green-600 hover:bg-green-700 disabled:bg-gray-600 text-white px-4 py-1.5 rounded-lg transition-colors text-sm flex items-center gap-1"
                  >
                    {approving === blog.id ? (
                      <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-white"></div>
                    ) : null}
                    Approve
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Blog List */}
      {blogs.length === 0 ? (
        <div className="text-center py-12 bg-gray-800 rounded-lg border border-gray-700">
          <h3 className="text-xl font-semibold text-gray-300 mb-4">No Blog Posts Yet</h3>
          <p className="text-gray-400 mb-6">Create your first blog post to get started</p>
          <button
            onClick={() => navigate('/admin/blog/new')}
            className="bg-blue-600 hover:bg-blue-700 text-white px-6 py-3 rounded-lg transition-colors"
          >
            Create First Post
          </button>
        </div>
      ) : (
        <div className="bg-gray-800 rounded-lg border border-gray-700 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-700">
                <tr>
                  <th className="text-left py-4 px-6 text-gray-300 font-semibold">Title</th>
                  <th className="text-left py-4 px-6 text-gray-300 font-semibold">Author</th>
                  <th className="text-left py-4 px-6 text-gray-300 font-semibold">Status</th>
                  <th className="text-left py-4 px-6 text-gray-300 font-semibold">Created</th>
                  <th className="text-right py-4 px-6 text-gray-300 font-semibold">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-700">
                {blogs.map((blog) => (
                  <tr key={blog.id} className="hover:bg-gray-750">
                    <td className="py-4 px-6">
                      <div>
                        <h4 className="text-gray-100 font-medium mb-1">{blog.title}</h4>
                        <p className="text-gray-400 text-sm">
                          {truncateText(blog.excerpt || blog.body)}
                        </p>
                      </div>
                    </td>
                    <td className="py-4 px-6 text-gray-300">
                      {blog.author || 'TrendingChess Team'}
                    </td>
                    <td className="py-4 px-6">
                      <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${
                        blog.published 
                          ? 'bg-green-900 text-green-200' 
                          : 'bg-yellow-900 text-yellow-200'
                      }`}>
                        {blog.published ? 'Published' : 'Draft'}
                      </span>
                    </td>
                    <td className="py-4 px-6 text-gray-400 text-sm">
                      {formatDate(blog.createdAt)}
                    </td>
                    <td className="py-4 px-6">
                      <div className="flex items-center justify-end gap-2">
                        {blog.published && (
                          <button
                            onClick={() => navigate(`/blog/${blog.slug}`)}
                            className="text-blue-400 hover:text-blue-300 px-2 py-1 rounded transition-colors"
                            title="View"
                          >
                            👁
                          </button>
                        )}
                        <button
                          onClick={() => navigate(`/admin/blog/edit/${blog.id}`)}
                          className="text-green-400 hover:text-green-300 px-2 py-1 rounded transition-colors"
                          title="Edit"
                        >
                          ✏️
                        </button>
                        <button
                          onClick={() => handleDelete(blog.id)}
                          disabled={deleting}
                          className={`px-2 py-1 rounded transition-colors ${
                            deleteConfirm === blog.id
                              ? 'text-red-300 bg-red-900/50'
                              : 'text-red-400 hover:text-red-300'
                          }`}
                          title={deleteConfirm === blog.id ? 'Click again to confirm' : 'Delete'}
                        >
                          {deleteConfirm === blog.id ? '⚠️' : '🗑️'}
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default BlogDashboard;